/**
 * Wrapper Promise autour de navigator.geolocation.getCurrentPosition.
 *
 * Retourne null si la geoloc est refusée, indisponible, ou si on dépasse
 * le timeout. C'est volontaire : le service de pointage accepte de
 * créer un pointage sans coords (flagué côté DB). On ne bloque pas
 * l'agent qui doit absolument pointer.
 */
export interface AgentPosition {
  latitude: number;
  longitude: number;
  precisionMetres: number;
}

export type GeolocationFailureReason =
  | "unavailable"
  | "denied"
  | "timeout"
  | "unknown";

export interface GeolocationOutcome {
  position: AgentPosition | null;
  reason: GeolocationFailureReason | null;
}

const DEFAULT_TIMEOUT_MS = 10_000;
const DEFAULT_MAX_AGE_MS = 30_000;

export function tryGetCurrentPosition(
  opts: { timeoutMs?: number; maxAgeMs?: number } = {},
): Promise<GeolocationOutcome> {
  if (typeof navigator === "undefined" || !navigator.geolocation) {
    return Promise.resolve({ position: null, reason: "unavailable" });
  }
  return new Promise<GeolocationOutcome>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          position: {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            precisionMetres: Math.round(pos.coords.accuracy),
          },
          reason: null,
        });
      },
      (err) => {
        const reason: GeolocationFailureReason =
          err.code === err.PERMISSION_DENIED
            ? "denied"
            : err.code === err.TIMEOUT
              ? "timeout"
              : err.code === err.POSITION_UNAVAILABLE
                ? "unavailable"
                : "unknown";
        resolve({ position: null, reason });
      },
      {
        enableHighAccuracy: true,
        timeout: opts.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        maximumAge: opts.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
      },
    );
  });
}
